import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';

import * as Styled from '../LoyaltyPageSeparated.styles';

export type PaymentMethod = 'app' | 'cashier';

interface LoyaltyPaymentScreenProps {
  onSelectPaymentMethod: (method: PaymentMethod) => void;
}

const ArrowRight = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 11v5M12 7.5v.01"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const WalletIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="6"
      width="18"
      height="13"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M16 12h2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const GiftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const CashierIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="8"
      width="18"
      height="12"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M7 8V5a2 2 0 012-2h6a2 2 0 012 2v3M7 13h2M7 16h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const LoyaltyPaymentScreen: React.FC<LoyaltyPaymentScreenProps> = ({
  onSelectPaymentMethod,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const loyaltyOrderId = useSelector(selectors.loyaltyOrderId);
  const orderReceiptInfo = useSelector(selectors.orderReceiptInfo);

  const docId = chosenReceipt?.doc_id ?? orderReceiptInfo?.doc_id ?? '';

  const totalPrice = useMemo(
    () =>
      (chosenReceipt?.items ?? []).reduce(
        (acc, item) => acc + Number(item?.total_price ?? 0),
        0
      ),
    [chosenReceipt]
  );

  const formatNumber = (n: number) => Number(n || 0).toLocaleString('ru-RU');

  // Клиент выбрал оплату через приложение Janymda
  const handleChooseApp = () => {
    dispatch(
      actions.setPartnerPaymentMethod.request({
        orderId: loyaltyOrderId,
        postEffect: () => onSelectPaymentMethod('app'),
      })
    );
  };

  // Клиент выбрал оплату на кассе.
  // Регистрируем метод "на месте" (актуально, если до этого выбирали приложение
  // и вернулись назад), затем переходим на экран обработки.
  const handleChooseCashier = () => {
    dispatch(
      actions.setInPlacePaymentMethod.request({
        orderId: loyaltyOrderId,
        postEffect: () => onSelectPaymentMethod('cashier'),
      })
    );
  };

  return (
    <Styled.ApplyBlock>
      <Styled.BlockTitle style={{ textAlign: 'center' }}>
        {t('LoyaltySeparated.PAYMENT_SELECT_TITLE')}
      </Styled.BlockTitle>

      {/* Инфо по заказу */}
      <Styled.OrderInfoBar>
        <Styled.OrderInfoLeft>
          <div className="order-line">
            {t('LoyaltySeparated.RECEIPT_NUMBER_ID')} {docId}
          </div>
          <div className="order-line">
            {t('LoyaltySeparated.ORDER_NUMBER')} {loyaltyOrderId}
          </div>
        </Styled.OrderInfoLeft>
        <Styled.OrderInfoRight>
          <Styled.OrderInfoChip>
            <span className="chip-label">
              {t('LoyaltySeparated.ORDER_AMOUNT')}
            </span>
            <span className="chip-value">{formatNumber(totalPrice)} тг</span>
          </Styled.OrderInfoChip>
        </Styled.OrderInfoRight>
      </Styled.OrderInfoBar>

      <Styled.PaymentSelectGrid>
        {/* --- Оплата через Janymda --- */}
        <Styled.PaymentSelectCard accent="green">
          <Styled.PaymentSelectHead>
            <img
              src="/images/janymda-logo.svg"
              alt="janymda"
              width="154"
              height="154"
            />
            <Styled.PaymentSelectHeadText>
              <Styled.PaymentSelectTitle>
                {t('LoyaltySeparated.PAY_IN_APP_TITLE')}
              </Styled.PaymentSelectTitle>
              <Styled.BeelineBadge>
                {t('LoyaltySeparated.BEELINE_ONLY')}
              </Styled.BeelineBadge>

              <Styled.PaymentSelectDesc>
                <p>{t('LoyaltySeparated.PAY_IN_APP_DESC_1')}</p>
                <p>{t('LoyaltySeparated.PAY_IN_APP_DESC_2')}</p>
              </Styled.PaymentSelectDesc>
            </Styled.PaymentSelectHeadText>
          </Styled.PaymentSelectHead>

          <Styled.BalanceChip>
            <WalletIcon />
            {t('LoyaltySeparated.PAY_BY_BALANCE')}
          </Styled.BalanceChip>

          <Styled.InfoHintBar>
            <InfoIcon />
            {t('LoyaltySeparated.PAY_IN_APP_IDENTIFY_HINT')}
          </Styled.InfoHintBar>

          <Styled.BonusBlock>
            <Styled.BonusBlockHead>
              <GiftIcon />
              {t('LoyaltySeparated.BONUSES_FOR_CLIENT')}
            </Styled.BonusBlockHead>
            <Styled.BonusItems>
              <Styled.BonusItem>
                <span className="bonus-value">10%</span>
                <span className="bonus-label">
                  {t('LoyaltySeparated.CASHBACK_HINT')}
                </span>
              </Styled.BonusItem>
              <Styled.BonusItem>
                <span className="bonus-value">
                  {t('LoyaltySeparated.FIRST_ORDER_DISCOUNT')}
                </span>
                <span className="bonus-label">
                  {t('LoyaltySeparated.FIRST_ORDER_DISCOUNT_HINT')}
                </span>
              </Styled.BonusItem>
            </Styled.BonusItems>
          </Styled.BonusBlock>

          <Styled.SelectPaymentButton accent="green" onClick={handleChooseApp}>
            {t('LoyaltySeparated.CLIENT_CHOSE_APP')}
            <ArrowRight />
          </Styled.SelectPaymentButton>
        </Styled.PaymentSelectCard>

        {/* --- Оплата на кассе --- */}
        <Styled.PaymentSelectCard accent="blue">
          <Styled.CashierCenter>
            <Styled.CashierIconCircle>
              <CashierIcon />
            </Styled.CashierIconCircle>
            <Styled.PaymentSelectTitle>
              {t('LoyaltySeparated.PAY_AT_CASHIER_TITLE')}
            </Styled.PaymentSelectTitle>
            <Styled.PaymentSelectDesc style={{ textAlign: 'center' }}>
              <p>{t('LoyaltySeparated.PAY_AT_CASHIER_DESC')}</p>
            </Styled.PaymentSelectDesc>
            <Styled.PaymentSelectDesc style={{ alignSelf: 'flex-start' }}>
              {t('LoyaltySeparated.AVAILABLE_METHODS')}
            </Styled.PaymentSelectDesc>
            <Styled.PaymentMethodsList>
              <li>{t('LoyaltySeparated.METHOD_CASH')}</li>
              <li>{t('LoyaltySeparated.METHOD_CARD')}</li>
            </Styled.PaymentMethodsList>
          </Styled.CashierCenter>

          <Styled.SelectPaymentButton
            accent="blue"
            onClick={handleChooseCashier}
          >
            {t('LoyaltySeparated.CLIENT_CHOSE_CASHIER')}
            <ArrowRight />
          </Styled.SelectPaymentButton>
        </Styled.PaymentSelectCard>
      </Styled.PaymentSelectGrid>
    </Styled.ApplyBlock>
  );
};

export default LoyaltyPaymentScreen;
